/*
Purpose: Top navigation bar for global app sections.
Audience: All users.
What it does:
- Provides access to dashboard, cases, uploads, and account/profile
Interplay:
- Works alongside Sidebar for page-level navigation
*/
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './NavigationBar.css';
import AuthPortal from '../auth/AuthPortal';
import ProfileDropdown from '../auth/ProfileDropdown';

const NavigationBar = ({ user, onLogoutClick, onMenuClick, onCloseSidebar }) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const logoDropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleOpenLoginModal = () => {
    setIsAuthModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setIsAuthModalOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Navigate to public casefiles with search parameter
      navigate(`/public?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm(''); // Clear search after submission
      setIsSearchExpanded(false); // Close search on mobile after submit
    }
  };

  const handleSearchIconClick = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e);
    }
  };

  const handleDropdownToggle = () => {
    // On mobile, close the hamburger sidebar when opening the dropdown menu
    if (isMobile && !isDropdownOpen && onCloseSidebar) {
      onCloseSidebar();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
  };

  // Close Red Handed dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (logoDropdownRef.current && !logoDropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Close search when clicking outside
  useEffect(() => {
    if (!isSearchExpanded) return;

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSearchExpanded]);

  return (
    <>
      <nav className="navbar">
        {/* Left section: Menu button and Logo */}
        <div className="navbar-section navbar-left">
          <button onClick={onMenuClick} className="menu-button">
            &#9776;
          </button>
          <div className="logo-dropdown-container" ref={logoDropdownRef}>
            <button onClick={handleDropdownToggle} className="logo-button">
              Red Handed
              <span className={`caret ${isDropdownOpen ? 'caret-open' : ''}`}>&#9662;</span>
            </button>
            {isDropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-overlay" onClick={handleDropdownClose}></div>
                <div className="dropdown-content">
                  {/* Section 1: Core Navigation */}
                  <div className="dropdown-section">
                    <Link to="/bulletin" className={location.pathname.startsWith('/bulletin') ? 'active' : ''} onClick={handleDropdownClose}>Bulletin</Link>
                    <Link to="/dashboard" className={location.pathname.startsWith('/dashboard') ? 'active' : ''} onClick={handleDropdownClose}>Dashboard</Link>
                    <Link to="/licenses" className={location.pathname.startsWith('/licenses') ? 'active' : ''} onClick={handleDropdownClose}>📜 My Licenses</Link>
                  </div>
                  
                  <hr className="dropdown-divider" />
                  
                  {/* Section 2: General Information */}
                  <div className="dropdown-section">
                    <Link to="/about" className={location.pathname === '/about' ? 'active' : ''} onClick={handleDropdownClose}>About</Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Center section: The new search bar */}
        <div className="navbar-section navbar-center" ref={searchRef}>
          <button 
            className="search-icon-button"
            onClick={handleSearchIconClick}
            aria-label="Search"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </button>
          <form onSubmit={handleSearchSubmit} className={`search-form ${isSearchExpanded ? 'search-expanded' : ''}`}>
            <input 
              type="search" 
              placeholder="Search Casefiles..." 
              className="search-bar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
          </form>
        </div>

        {/* Right section: Action buttons */}
        <div className="navbar-section navbar-right">
          {user ? (
            <ProfileDropdown user={user} onLogoutClick={onLogoutClick} />
          ) : (
            <button onClick={handleOpenLoginModal} className="login-button">Login</button>
          )}
        </div>
      </nav>

      {isAuthModalOpen && <AuthPortal onClose={handleCloseLoginModal} />}
    </>
  );
};

export default NavigationBar;